/* ============================================================
   Esse Werks — customer data client (Phase 7).

   Owns reads + writes to the three customer-owned JSON metafields:
     ew_.saved_configurations  → My Builds (saved configurator state)
     ew_.garage                → My Garage (vehicles)
     ew_.build_history         → My Builds (completed; Phase 8 webhook
                                  populates server-side, this client
                                  only reads)
     ew_.tune_licenses         → My Licenses (Phase 8 webhook populates)

   Storage strategy
   ----------------
   New Customer Accounts (passwordless) does not give the storefront a
   customer access token that a Storefront API mutation can use, and
   the Customer Account API at shopify.com/<shop-id> is a different
   origin. Direct browser-side metafield writes to a customer record
   require setting up either:
     (a) an app proxy that forwards to Admin API, or
     (b) a Storefront-API-with-customer-access-token flow that requires
         customer-account-creator-side setup we don't have yet.

   Phase 7 ships the working UX against **localStorage** keyed by
   customer ID. Server-rendered metafield data (Liquid {{ customer
   .metafields.ew_.* }}) is merged in for read display so that future
   server-side writes (e.g. Phase 8 order webhook populating
   build_history) surface automatically without client changes.

   Mutating server-side persistence (so a build saved on phone surfaces
   on desktop) is a Phase 8 follow-up: the order webhook already needs
   Admin API write access for build_history, and that same access can
   accept a small app-proxy endpoint for saved_configurations + garage.

   Until then: writes are local-device-only, reads are union(server, local).
   ============================================================ */
(function () {
  'use strict';

  function customerId() {
    return (typeof window.ewCustomerId === 'number') ? window.ewCustomerId : null;
  }

  function lsKey(field) {
    var id = customerId();
    if (!id) return null;
    return 'ew_' + field + ':' + id;
  }

  /* ---- low-level localStorage helpers ---- */
  function lsRead(field) {
    var k = lsKey(field);
    if (!k) return [];
    try {
      var raw = localStorage.getItem(k);
      return raw ? JSON.parse(raw) : [];
    } catch (_) { return []; }
  }
  function lsWrite(field, arr) {
    var k = lsKey(field);
    if (!k) return false;
    try { localStorage.setItem(k, JSON.stringify(arr)); return true; }
    catch (_) { return false; }
  }

  /* ---- server-rendered data is passed in via <script type="application/json" data-ew-customer-bootstrap> ---- */
  function bootstrap() {
    var el = document.querySelector('[data-ew-customer-bootstrap]');
    if (!el) return {};
    try { return JSON.parse(el.textContent || '{}'); } catch (_) { return {}; }
  }

  /* Merge server (authoritative for build_history + tune_licenses) and
     local (authoritative for saved_configurations + garage in Phase 7).
     Dedupe by id where applicable. */
  function mergeArrays(server, local, idKey) {
    var s = Array.isArray(server) ? server : [];
    var l = Array.isArray(local) ? local : [];
    if (!idKey) return s.concat(l);
    var seen = Object.create(null);
    var out = [];
    [].concat(s, l).forEach(function (item) {
      if (!item) return;
      var id = item[idKey];
      if (id !== undefined && id !== null) {
        if (seen[id]) return;
        seen[id] = true;
      }
      out.push(item);
    });
    return out;
  }

  /* ============================================================
     SAVED CONFIGURATIONS (My Builds — saved tab)
     ============================================================ */

  function getSavedBuilds() {
    var b = bootstrap();
    var server = (b.saved_configurations) || [];
    var local = lsRead('saved_configurations');
    return mergeArrays(server, local, 'build_id');
  }

  /* Append a saved build. Returns Promise<{ok, count}>. */
  function saveBuild(entry) {
    if (!entry || typeof entry !== 'object') {
      return Promise.reject(new Error('saveBuild: entry required'));
    }
    if (!customerId()) {
      return Promise.reject(new Error('Not signed in'));
    }
    if (!entry.saved_at) entry.saved_at = new Date().toISOString();
    if (!entry.build_id) {
      entry.build_id = 'b_' + Math.random().toString(36).slice(2, 10) + '_' + Date.now().toString(36);
    }
    var existing = lsRead('saved_configurations');
    existing.push(entry);
    /* Cap at 10 per the demo copy ("Save up to 10 builds"). */
    if (existing.length > 10) existing = existing.slice(-10);
    lsWrite('saved_configurations', existing);
    return Promise.resolve({ ok: true, count: existing.length, build_id: entry.build_id });
  }

  function deleteBuild(buildId) {
    if (!buildId) return Promise.reject(new Error('deleteBuild: buildId required'));
    var existing = lsRead('saved_configurations');
    var filtered = existing.filter(function (b) { return b.build_id !== buildId; });
    lsWrite('saved_configurations', filtered);
    return Promise.resolve({ ok: true, count: filtered.length });
  }

  /* GARAGE-4: the "U" in the saved-build CRUD — patch a saved build by
     build_id (e.g. rename). Mirrors updateVehicle: local-store-only write
     (Phase-7 model); a server-bootstrap-only build won't be found. */
  function updateBuild(buildId, patch) {
    if (!buildId) return Promise.reject(new Error('updateBuild: buildId required'));
    var existing = lsRead('saved_configurations');
    var found = false;
    existing = existing.map(function (b) {
      if (b.build_id === buildId) {
        found = true;
        return Object.assign({}, b, patch, { build_id: buildId, updated_at: new Date().toISOString() });
      }
      return b;
    });
    if (!found) return Promise.reject(new Error('Build not found'));
    lsWrite('saved_configurations', existing);
    return Promise.resolve({ ok: true });
  }

  /* ============================================================
     GARAGE (My Garage)
     ============================================================ */

  function getGarage() {
    var b = bootstrap();
    var server = (b.garage) || [];
    var local = lsRead('garage');
    return mergeArrays(server, local, 'vehicle_id');
  }

  function addVehicle(v) {
    if (!v || typeof v !== 'object') return Promise.reject(new Error('addVehicle: vehicle required'));
    if (!customerId()) return Promise.reject(new Error('Not signed in'));
    if (!v.vehicle_id) {
      v.vehicle_id = 'v_' + Math.random().toString(36).slice(2, 10) + '_' + Date.now().toString(36);
    }
    if (!v.added_at) v.added_at = new Date().toISOString();
    var existing = lsRead('garage');
    existing.push(v);
    lsWrite('garage', existing);
    return Promise.resolve({ ok: true, count: existing.length, vehicle_id: v.vehicle_id });
  }

  function updateVehicle(vehicleId, patch) {
    if (!vehicleId) return Promise.reject(new Error('updateVehicle: vehicleId required'));
    var existing = lsRead('garage');
    var found = false;
    existing = existing.map(function (v) {
      if (v.vehicle_id === vehicleId) {
        found = true;
        return Object.assign({}, v, patch, { vehicle_id: vehicleId, updated_at: new Date().toISOString() });
      }
      return v;
    });
    if (!found) return Promise.reject(new Error('Vehicle not found'));
    lsWrite('garage', existing);
    return Promise.resolve({ ok: true });
  }

  function removeVehicle(vehicleId) {
    if (!vehicleId) return Promise.reject(new Error('removeVehicle: vehicleId required'));
    var existing = lsRead('garage');
    var filtered = existing.filter(function (v) { return v.vehicle_id !== vehicleId; });
    lsWrite('garage', filtered);
    return Promise.resolve({ ok: true, count: filtered.length });
  }

  /* ============================================================
     READ-ONLY (Phase 8 webhook populates these)
     ============================================================ */

  function getBuildHistory() {
    var b = bootstrap();
    return Array.isArray(b.build_history) ? b.build_history : [];
  }

  function getTuneLicenses() {
    var b = bootstrap();
    return Array.isArray(b.tune_licenses) ? b.tune_licenses : [];
  }

  /* ============================================================
     GAR-1.6 — login reconcile (local-device only, no server writes)

     When a previously-anonymous visitor signs in, their anon active
     vehicle lives in localStorage['ew_active_vehicle'] (written by
     ew-vehicle-resolver.js). On a logged-in page we reconcile ONCE:

       - Server garage has >=1 vehicle  -> server data is authoritative.
         Do NOT carry a stale anon vehicle in (it would duplicate/clobber
         the user's real garage). We leave server data untouched.
       - Server garage is EMPTY         -> the just-anon user has no
         server vehicles yet; carry their anon active vehicle into the
         (local) customer garage via addVehicle so they keep their car
         after signing up. Skipped if an identical vehicle already sits
         in the local garage (idempotent across reloads).

     NEVER deletes or overwrites server-side data. addVehicle() only
     writes the local, customer-id-keyed garage store — same persistence
     all Phase-7 writes use. mergeArrays() then unions server + local for
     display, so a server save always survives.
     ============================================================ */
  function readAnonActive() {
    try { return JSON.parse(localStorage.getItem('ew_active_vehicle') || 'null'); } catch (_) { return null; }
  }
  /* Identity compares the engine FAMILY, never the raw engine_code.
     LP ruling 2026-08-11: platform is for family logic; engine_code never is.

     WHY: WO-2B-v3 Phase 0A rewrote engine_code from family form to full form on
     103 chassis (`B58` -> `B58B30O1`). A garage row saved before that migration
     would never again equal the same car resolved after it, so
     reconcileAnonVehicle re-added the identical vehicle on every single reload.

     There is no `platform` field on a persisted vehicle — ew-vehicle-resolver.js
     stores only `code` and `engine_code` (deliberately raw; see its P1c-2 note,
     the raw code is the identity that drives tune resolution and must not be
     mutated). `platform` IS the folded family, so the ruling is implemented with
     EWVehicleResolver.engineFamily() — the one canonical normalizer, which folds
     B46->B48 and N26->N20 exactly as `platform` does. Reading `a.platform`
     literally would compare undefined === undefined, i.e. always true, silently
     collapsing this to chassis-code-only and merging an F22 230i with an F22 M240i.

     If the resolver has not loaded, fall back to raw comparison: behaviour is
     then exactly what it is today, never worse. No private copy of the regex
     lives here — same posture as ew-fitment-filter.js. */
  function vehicleFamily(v) {
    var raw = (v.engine_code || v.engine || '').toUpperCase();
    var R = window.EWVehicleResolver;
    if (R && typeof R.engineFamily === 'function') {
      return String(R.engineFamily(raw) || raw).toUpperCase();
    }
    return raw;
  }
  function sameVehicle(a, b) {
    if (!a || !b) return false;
    var ac = (a.code || a.chassis_code || a.chassis || '').toUpperCase();
    var bc = (b.code || b.chassis_code || b.chassis || '').toUpperCase();
    return ac === bc && vehicleFamily(a) === vehicleFamily(b) && !!ac;
  }
  function reconcileAnonVehicle() {
    if (!customerId()) return;                 /* logged-in only */
    var serverGarage = (bootstrap().garage) || [];
    if (Array.isArray(serverGarage) && serverGarage.length >= 1) return; /* server authoritative */
    var anon = readAnonActive();
    if (!anon) return;
    /* Don't double-add across reloads: bail if local garage already has it. */
    var local = lsRead('garage');
    for (var i = 0; i < local.length; i++) { if (sameVehicle(local[i], anon)) return; }
    addVehicle({
      code: anon.code || anon.chassis_code || anon.chassis || '',
      engine_code:  anon.engine_code  || anon.engine  || '',
      /* F-05 — carry the year ONLY with provenance (year_known): a legacy
         anon value's unprovenanced year was the fabricated band end. */
      year:  (anon.year_known === true && anon.year) || null,
      make:  anon.make  || null,
      model: anon.model || null,
      trim:  anon.trim  || null,
      label: anon.label || '',
      source: 'anon-reconcile'
    });
  }
  try { reconcileAnonVehicle(); } catch (_) {}

  /* ---- public API ---- */
  window.ewCustomer = {
    isAuthenticated: function () { return !!customerId(); },
    customerId:      customerId,
    getSavedBuilds:  getSavedBuilds,
    saveBuild:       saveBuild,
    deleteBuild:     deleteBuild,
    updateBuild:     updateBuild,
    getGarage:       getGarage,
    addVehicle:      addVehicle,
    updateVehicle:   updateVehicle,
    removeVehicle:   removeVehicle,
    getBuildHistory: getBuildHistory,
    getTuneLicenses: getTuneLicenses,
  };
})();
